import { useCookie } from 'nuxt/app';
import { computed, readonly, ref } from 'vue';

export default function useTimer(
  options: {
    cookieName?: string;
    // Cookie max age in seconds
    cookieMaxAge?: number;
    onComplete?: () => void;
  } = {},
) {
  const { cookieName, cookieMaxAge, onComplete } = options;

  const timeLeft = ref(0);
  const isRunning = ref(false);
  let timerInterval: NodeJS.Timeout | null = null;

  // Cookie for persistence (optional)
  const timerCookie = cookieName
    ? useCookie<number | null>(cookieName, {
        default: () => null,
        maxAge: cookieMaxAge,
      })
    : ref(null);

  const canStart = computed(() => !isRunning.value && timeLeft.value === 0);

  const clearTimerInterval = (): void => {
    if (timerInterval) {
      clearInterval(timerInterval);
      timerInterval = null;
    }
    isRunning.value = false;
  };

  const startInterval = (): void => {
    if (timerInterval) {
      clearTimerInterval();
    }

    isRunning.value = true;
    timerInterval = setInterval(() => {
      timeLeft.value--;

      if (timeLeft.value <= 0) {
        stop();
        onComplete?.();
      }
    }, 1000);
  };

  const start = (durationSeconds: number): void => {
    if (durationSeconds <= 0) return;

    timeLeft.value = durationSeconds;

    if (cookieName && timerCookie) {
      const expiryTime = Date.now() + durationSeconds * 1000;
      timerCookie.value = expiryTime;
    }

    startInterval();
  };

  const stop = (): void => {
    clearTimerInterval();
    timeLeft.value = 0;

    if (cookieName && timerCookie) {
      timerCookie.value = null;
    }
  };

  const pause = (): void => {
    clearTimerInterval();
  };

  const resume = (): void => {
    if (timeLeft.value > 0) {
      startInterval();
    }
  };

  const initialize = (): void => {
    if (!cookieName || !timerCookie?.value) return;

    const savedExpiry = timerCookie.value;
    const now = Date.now();
    const remaining = Math.max(0, Math.floor((savedExpiry - now) / 1000));

    if (remaining > 0) {
      timeLeft.value = remaining;
      startInterval();
    } else {
      clear();
    }
  };

  const clear = (): void => {
    clearTimerInterval();
    timeLeft.value = 0;

    if (cookieName && timerCookie) {
      timerCookie.value = null;
    }
  };

  return {
    timeLeft: readonly(timeLeft),
    isRunning: readonly(isRunning),
    canStart,

    start,
    stop,
    pause,
    resume,
    initialize,
    clear,
  };
}
